/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import moment from "moment-timezone";
import {
  createAction,
  deleteRequest,
  escapeFilterValue,
  getRequest,
  postRequest,
  startLoading,
  stopLoading
} from "openstack-uicore-foundation/lib/utils/actions";
import { getAccessTokenSafely } from "../utils/methods";
import { DEFAULT_PER_PAGE, MIN_PER_PAGE } from "../utils/constants";
import { snackbarErrorHandler } from "./base-actions";

export const REQUEST_SHOWS = "REQUEST_SHOWS";
export const RECEIVE_SHOWS = "RECEIVE_SHOWS";
export const REQUEST_SHOW_ACCESS = "REQUEST_SHOW_ACCESS";
export const RECEIVE_SHOW_ACCESS = "RECEIVE_SHOW_ACCESS";
export const RECEIVE_SHOW_METADATA = "RECEIVE_SHOW_METADATA";
export const RECEIVE_REQUESTS = "RECEIVE_REQUESTS";
export const REQUEST_DELETED = "REQUEST_DELETED";
export const REQUEST_ALL_SHOWS = "REQUEST_ALL_SHOWS";
export const RECEIVE_ALL_SHOWS = "RECEIVE_ALL_SHOWS";
export const RECEIVE_ALL_SPONSORS = "RECEIVE_ALL_SPONSORS";
export const ACCESS_REQUEST_ADDED = "ACCESS_REQUEST_ADDED";
export const RECEIVE_SHOW_LOCATIONS = "RECEIVE_SHOW_LOCATIONS";

export const getRequests =
  (page = 1, perPage = DEFAULT_PER_PAGE) =>
  async (dispatch) => {
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      page,
      per_page: perPage,
      access_token: accessToken,
      filter: "status==pending",
      order: "created",
      expand: "summit,sponsor"
    };

    return (
      getRequest(
        null,
        createAction(RECEIVE_REQUESTS),
        `${window.SPONSOR_USERS_API_BASE_URL}/api/v1/access-requests/me`,
        snackbarErrorHandler
      )(params)(dispatch)
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .then(() => {
          dispatch(stopLoading());
        })
    );
  };

export const deleteAccessRequest = (requestId) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return (
    deleteRequest(
      null,
      createAction(REQUEST_DELETED)({ requestId }),
      `${window.SPONSOR_USERS_API_BASE_URL}/api/v1/access-requests/${requestId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
      .catch(() => {})
      .then(() => {
        dispatch(stopLoading());
      })
  );
};

export const getShows =
  (
    filters = null,
    page = 1,
    perPage = DEFAULT_PER_PAGE,
    order = "summit_name",
    orderDir = 1
  ) =>
  async (dispatch) => {
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    const params = {
      page,
      per_page: perPage,
      access_token: accessToken,
      expand: "summit,sponsor,sponsor.sponsorships,sponsor.sponsorships.addons"
    };

    if (filters?.term) {
      const escapedTerm = escapeFilterValue(filters.term);
      filter.push(
        `sponsor_company_name@@${escapedTerm},summit_name@@${escapedTerm}`
      );
    }

    if (filters?.hide_past_events) {
      const now = moment().unix(); // now epoch
      filter.push(`summit_end_date>${now}`);
    }

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return (
      getRequest(
        createAction(REQUEST_SHOWS),
        createAction(RECEIVE_SHOWS),
        `${window.SPONSOR_USERS_API_BASE_URL}/api/v1/permissions/me`,
        snackbarErrorHandler,
        { order, orderDir, filters }
      )(params)(dispatch)
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .then(() => {
          dispatch(stopLoading());
        })
    );
  };

export const getShowAccess = (summitId, sponsorId) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand: "summit,sponsor,sponsor.sponsorships,sponsor.sponsorships.addons"
  };

  return getRequest(
    createAction(REQUEST_SHOW_ACCESS),
    createAction(RECEIVE_SHOW_ACCESS),
    `${window.SPONSOR_USERS_API_BASE_URL}/api/v1/permissions/me/summits/${summitId}/sponsors/${sponsorId}`,
    snackbarErrorHandler
  )(params)(dispatch).then(() => {
    dispatch(stopLoading());
  });
};

export const getAllShows =
  (term = "", page = 1) =>
  async (dispatch) => {
    const filter = ["is_access_request_enabled==1"];

    dispatch(startLoading());
    const accessToken = await getAccessTokenSafely();

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm}`);
    }

    const params = {
      access_token: accessToken,
      page,
      per_page: MIN_PER_PAGE,
      order: "-start_date",
      expand: "summit",
      fields: "id,name",
      relations: "none"
    };

    params["filter[]"] = filter;

    return (
      getRequest(
        createAction(REQUEST_ALL_SHOWS),
        createAction(RECEIVE_ALL_SHOWS),
        `${window.SPONSOR_USERS_API_BASE_URL}/api/v1/shows-metadata`,
        snackbarErrorHandler,
        { term, page }
      )(params)(dispatch)
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .then(() => {
          dispatch(stopLoading());
        })
    );
  };

export const getShowLocations = (summitId) => async (dispatch) => {
  const params = {
    expand: "locations",
    fields:
      "none,locations.id,locations.is_main,locations.short_name,locations.address_1,locations.address_2,locations.city,locations.name,locations.state,locations.zip_code,locations.country",
    relations: "locations,locations.none"
  };
  dispatch(startLoading());
  return getRequest(
    null,
    createAction(RECEIVE_SHOW_LOCATIONS),
    `${window.API_BASE_URL}/api/public/v1/summits/${summitId}`,
    snackbarErrorHandler
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

export const getShowMetadata = (summitId) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  return getRequest(
    null,
    createAction(RECEIVE_SHOW_METADATA),
    `${window.PURCHASES_API_URL}/api/v1/shows-metadata/${summitId}`,
    snackbarErrorHandler
  )(params)(dispatch).then(() => {
    dispatch(stopLoading());
  });
};

export const getAllSponsors =
  (summitId, page = 1) =>
  async (dispatch) => {
    dispatch(startLoading());

    const params = {
      page,
      per_page: MIN_PER_PAGE,
      order: "sponsorship_size",
      fields:
        "id,company.id,company.name,sponsorships.id,sponsorships.type.none,sponsorships.type.type.id,sponsorships.type.type.name,sponsorships.type.type.order",
      expand: "company,sponsorships,sponsorships.type,sponsorships.type.type",
      relations:
        "sponsorships,sponsorships.type,sponsorships.type.type,company.none"
    };

    return (
      getRequest(
        null,
        createAction(RECEIVE_ALL_SPONSORS),
        `${window.API_BASE_URL}/api/public/v1/summits/all/${summitId}/sponsors`,
        snackbarErrorHandler
      )(params)(dispatch)
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .then(() => {
          dispatch(stopLoading());
        })
    );
  };

export const requestAccess = (summitId, sponsor) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();
  const request = { summit_id: summitId };

  const params = {
    access_token: accessToken,
    expand: "summit"
  };

  if (sponsor.id) {
    request.company_id = sponsor.id;
  } else {
    request.company_name = sponsor.name;
  }

  dispatch(startLoading());

  return (
    postRequest(
      null,
      createAction(ACCESS_REQUEST_ADDED),
      `${window.SPONSOR_USERS_API_BASE_URL}/api/v1/access-requests`,
      request,
      snackbarErrorHandler
    )(params)(dispatch)
      // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};
